import { API_BASE_URL, isSimple, isAdvanced } from 'config'
import React from 'react'

import { kea } from 'kea'
import { put, call } from 'redux-saga/effects'

import PropTypes from 'prop-types'
import axios from 'axios'
import DataLogic from 'containers/App/logic'
import { Validations } from 'validations'

import omit from 'lodash/omit'
import find from 'lodash/find'
import first from 'lodash/first'
import forEach from 'lodash/forEach'

import EgIcon from './images/electricityplusgas.component.svg'
import EIcon from './images/electricity.component.svg'
import GIcon from './images/gas.component.svg'

const DISTRICTS_ENDPOINT = `${API_BASE_URL}/public/district/`
const CITIES_ENDPOINT = `${API_BASE_URL}/public/municipality/`
const ENTEPRISE_START = `${API_BASE_URL}/public/simulator/enterprise_start/`

const fields = {
  'offerType': [
    Validations.isUndefined
  ],
  'cae': [
    Validations.isNull
  ],
  'work_hours': [
    Validations.isNull
  ],
  'district': [
    Validations.isUndefined
  ],
  'municipality': [
    Validations.isUndefined
  ]
}

/* eslint-disable camelcase */
export default kea({
  path: () => ['scenes', 'company-profile'],

  actions: () => ({
    setDistricts: (districts) => ({ districts }),
    setData: (data) => ({ data }),
    setCities: (cities) => ({ cities }),
    setCurrentLocation: (currentLocation) => ({ currentLocation }),
    setInvalid: (invalid) => ({ invalid }),
    setCae: (cae) => ({ cae }),

    removeCities: (cities) => ({ cities }),

    fetchDistricts: (limit) => ({ limit }),
    fetchCities: (district) => ({ district }),
    fetchData: () => ({ }),
    changeLoading: () => ({ }),

    setDataFetchError: (fetchDataError) => ({ fetchDataError }),
    setDistrictsFetchError: (fetchDistrictError) => ({ fetchDistrictError }),
    setCitiesFetchError: (fetchCitiesError) => ({ fetchCitiesError })
  }),

  reducers: ({ actions }) => ({
    currentLocalization: [null, PropTypes.any, {
      [actions.setCurrentLocation]: (state, payload) => payload.currentLocation
    }],

    districts: [[], PropTypes.array, {
      [actions.setDistricts]: (state, payload) => ([...payload.districts])
    }],

    cities: [[], PropTypes.array, {
      [actions.setCities]: (state, payload) => ([...payload.cities]),
      [actions.removeCities]: (state, payload) => omit(state, payload.cities)
    }],

    data: [{}, PropTypes.object, {
      [actions.setData]: (state, payload) => ({...payload.data})
    }],

    cae: [{}, PropTypes.object, {
      [actions.setCae]: (state, payload) => payload.cae.cae
    }],

    fields: [fields, PropTypes.object, {}],

    invalid: [{}, PropTypes.array, {
      [actions.setInvalid]: (_, payload) => payload.invalid
    }],

    loading: [true, PropTypes.bool, {
      [actions.changeLoading]: () => true,
      [actions.fetchData]: () => true,
      [actions.fetchDistricts]: () => true,

      [actions.setDistricts]: () => false,
      [actions.setData]: () => false,
      [actions.setDataFetchError]: () => false
    }]

  }),

  selectors: ({ selectors }) => ({
    selectedCae: [
      () => [selectors.data, selectors.cae],
      (data, cae) => {
        return data && data.caes && data.caes.find(({value}) => value === cae)
      }
    ]
  }),

  takeLatest: ({ actions, workers }) => ({
    [actions.fetchDistricts]: workers.fetchDistricts,
    [actions.setDistricts]: workers.setFromLocation,
    [actions.setCities]: workers.setMunicipalityFromLocation,
    [actions.setCurrentLocation]: workers.setFromLocation,
    [actions.setDistrict]: workers.fetchCities,
    [actions.fetchCities]: workers.fetchCities,
    [actions.fetchData]: workers.fetchData
  }),

  start: function * () {
    const { fetchDistricts, fetchData } = this.actions
    const { simulationFinished } = yield DataLogic.get('finished')
    const values = yield DataLogic.get('values')

    // Only fetch the data once
    // No need to do it when whe hit Prev for example
    if (simulationFinished || !values.simulationCode) {
      yield put(fetchDistricts(100))
      yield put(fetchData())
    }
  },

  workers: {
    /**
     * Fetches the needed data
     * @param {*} action
     */
    * fetchData (action) {
      const { setData, setDataFetchError, setValues } = this.actions
      const { simulationFinished } = yield DataLogic.get('finished')
      const settings = yield DataLogic.get('settings')
      const params = { format: 'json', type: isSimple ? 's' : isAdvanced ? 'a' : 'e' }

      try {
        const data = yield call(axios.post, ENTEPRISE_START, params)

        const caes = data.data &&
          data.data.caes
          .map(({id, name, type, code}) => ({
            label: code + ' - ' + name,
            value: id,
            type
          }))

        const enterpriseProfiles = data.data &&
          data.data.enterprise_profiles
          .sort((a, b) => a.order - b.order)
          .map(({id, name, description}) => ({
            text: name,
            value: id,
            description
          }))

        const offerTypes = data.data &&
          data.data.offer_types
          .sort((a, b) => a.order - b.order)
          .map(({id, slug, name}) => {
            let icon
            switch (slug) {
              case 'e':
                icon = <EIcon />
                break
              case 'g':
                icon = <GIcon />
                break
              case 'eg':
                icon = <EgIcon />
                break
              default:
                icon = <EIcon />
            }
            return {
              text: (name + '').replace(' + ', '<br />+<br />'),
              value: id,
              icon,
              id,
              slug
            }
          })

        // Set the default values after getting the data
        const simulationCode = data.data.simulation_code
        let defaultValues = {}

        if (!simulationFinished && !settings.isFromProfile) {
          defaultValues = {
            simulationCode,
            offerType: find(offerTypes, ({slug}) => slug === 'eg').id,
            cae: null,
            enterprise_profile: first(enterpriseProfiles).value
          }
        } else {
          // Reset simulation code
          defaultValues = {
            simulationCode
          }
        }

        yield put(setValues(defaultValues))
        yield put(setData({
          caes,
          offerTypes,
          enterpriseProfiles
        }))
      } catch (error) {
        yield put(setDataFetchError(error))
      }
    },

    /**
     * Fetchs all Districts
     * @param {*} action
     */
    * fetchDistricts (action) {
      const { limit } = action.payload
      const { setDistricts, setDistrictsFetchError } = this.actions
      const params = {limit, format: 'json'}

      try {
        const districts = yield call(axios.get, DISTRICTS_ENDPOINT, { params })
        const filteredDistricts = districts.data.results.map(({ id, name }) => ({
          label: name,
          value: id
        }))

        yield put(setDistricts(filteredDistricts))
      } catch (error) {
        yield put(setDistrictsFetchError(error))
      }
    },

    /**
     * Fetches all municipalities from a district
     * @param {*} action
     */
    * fetchCities (action) {
      const { district } = action.payload
      const { setCities, setCitiesFetchError } = this.actions
      const params = {limit: 1000, format: 'json', district}

      try {
        const cities = yield call(axios.get, CITIES_ENDPOINT, { params })
        let { results } = cities.data

        results = results
          .map(({ id, name, district }) => ({
            value: id,
            label: name,
            district: district.id
          }))

        yield put(setCities(results))
      } catch (error) {
        yield put(setCitiesFetchError(error))
      }
    },

    * setFromLocation () {
      const { setValues } = this.actions

      const districts = yield this.get('districts')
      const currentLocation = yield this.get('currentLocalization')

      if (currentLocation && !!currentLocation.length) {
        let distrito = null
        forEach(currentLocation, ({ long_name }) => {
          let find = districts && districts.find(({ label }) => label.toLowerCase() === long_name.toLowerCase())
          if (find) {
            distrito = find
            return false
          }
        })
        if (distrito) {
          yield put(setValues({ district: distrito.value }))
        }
      }
    },

    * setMunicipalityFromLocation () {
      const { setValues } = this.actions
      const cities = yield this.get('cities')
      const currentLocation = yield this.get('currentLocalization')
      let cidade = null

      if (currentLocation && !!currentLocation.length) {
        // Search First for administrative area level 2 and 3
        forEach(currentLocation, (location) => {
          let find = cities && cities.find(({ label }) => label.toLowerCase() === location.long_name.toLowerCase() && (location.types[0] === 'administrative_area_level_2' || location.types[0] === 'administrative_area_level_3'))
          if (find) {
            cidade = find
            return false
          }
        })

        // If search is empty search by the name
        if (!cidade) {
          forEach(currentLocation, (location) => {
            let find = cities && cities.find(({ label }) => label.toLowerCase() === location.long_name.toLowerCase())
            if (find) {
              cidade = find
              return false
            }
          })
        }

        if (cidade) {
          yield put(setValues({ municipality: cidade.value }))
        }
      }
    }
  },

  connect: {
    actions: [DataLogic, ['setValue', 'setValues', 'setFinished', 'iframed']],
    props: [DataLogic, ['values', 'finished', 'translations']]
  }

})



// WEBPACK FOOTER //
// ./src/containers/CompanyProfile/logic.js